import Table, { CellProps } from 'rsuite/Table';
import Toggle from 'rsuite/Toggle';
import React, { useMemo } from 'react';
import { CommonService } from '../../../services/common.service';
import Popover from 'rsuite/Popover';
import Dropdown from 'rsuite/Dropdown';
import Whisper from 'rsuite/Whisper';
import IconButton from 'rsuite/IconButton';
import { useNavigate } from 'react-router-dom';
import {
  CleanPackageMethod,
  RbacObject,
} from '../../../export-types/cleaned-types';
import { WhisperSpeaker } from '../../../utils/whisper-speaker';
import useSWR, { SWRConfiguration } from 'swr';
import { fetcher } from '../../../services/api';
import { SupplierCell } from '../../common/table/supplier-cell';
import { Rbac } from '../../common/Rbac';
import {
  SupplierSelect,
  useSelectSupplierQs,
} from '../purchase-order/supplier-select';

export function usePackageMethods(config?: SWRConfiguration) {
  const { data, isLoading, mutate } = useSWR<Array<CleanPackageMethod>>(
    '/package-method',
    fetcher,
    config,
  );
  return { data, isLoading, mutate };
}

export const PackageMethodsTab = () => {
  const navigate = useNavigate();
  const [selectSupplier] = useSelectSupplierQs();
  const { data: packageMethods, isLoading, mutate } = usePackageMethods();

  const updatedPackageMethods = useMemo(() => {
    if (packageMethods && selectSupplier) {
      return packageMethods.filter(
        (item) => item.supplier && item.supplier.id === selectSupplier,
      );
    } else {
      return packageMethods;
    }
  }, [packageMethods, selectSupplier]);
  function checkPackageMethod(id: string, disabled: boolean) {
    CommonService.updatePackageMethod(id, { disabled }).then(() => {
      mutate();
    });
  }

  const DisableCell = ({ rowData, ...props }: CellProps<CleanPackageMethod>) =>
    rowData && (
      <Table.Cell {...props}>
        <Toggle
          checked={!rowData.disabled}
          onChange={(val) => checkPackageMethod(rowData.id, !val)}
        />
      </Table.Cell>
    );

  const renderMenu =
    (packageMethod: CleanPackageMethod): WhisperSpeaker =>
    ({ onClose, left, top, className }, ref) => {
      const handleSelect = (eventKey: number | string | undefined) => {
        onClose();

        if (eventKey === 1) {
          navigate(`/deliveries/package-method`, { state: packageMethod });
        } else if (eventKey === 2) {
          const result = window.confirm(
            `Вы действительно хотите удалить способ упаковки «${packageMethod.name}»?`,
          );
          if (!result) {
            return;
          }
          CommonService.deletePackageMethod(packageMethod.id).then(() =>
            mutate(),
          );
        }
      };

      return (
        <Popover ref={ref} className={className} style={{ left, top }} full>
          <Dropdown.Menu onSelect={handleSelect}>
            <Dropdown.Item eventKey={1}>
              <i className="bi bi-pencil" />
              <span className="ps-2">Изменить</span>
            </Dropdown.Item>
            <Dropdown.Item eventKey={2}>
              <i className="bi bi-trash" />
              <span className="ps-2">Удалить</span>
            </Dropdown.Item>
          </Dropdown.Menu>
        </Popover>
      );
    };

  const ActionCell = ({
    rowData,
    dataKey,
    ...props
  }: CellProps<CleanPackageMethod>) => {
    return (
      rowData && (
        <Table.Cell
          {...props}
          className="link-group no-padding-cell pt-2"
          align="right"
        >
          <Whisper
            placement="leftStart"
            trigger="click"
            speaker={renderMenu(rowData)}
          >
            <IconButton
              appearance="subtle"
              icon={<i className="bi bi-three-dots" />}
            />
          </Whisper>
        </Table.Cell>
      )
    );
  };

  return (
    <>
      <div className="d-flex w-75 mr-auto z-3">
        <Rbac object={RbacObject.Supplier} action={'read:admin'}>
          <SupplierSelect />
        </Rbac>
      </div>
      <Table
        data={updatedPackageMethods}
        headerHeight={57}
        autoHeight
        loading={isLoading}
      >
        <Table.Column width={150}>
          <Table.HeaderCell>Поставщик</Table.HeaderCell>
          <SupplierCell />
        </Table.Column>
        <Table.Column flexGrow={0} width={250}>
          <Table.HeaderCell>Название</Table.HeaderCell>
          <Table.Cell dataKey="name" />
        </Table.Column>
        <Table.Column flexGrow={1} minWidth={100}>
          <Table.HeaderCell>Комментарий</Table.HeaderCell>
          <Table.Cell dataKey="comment" />
        </Table.Column>
        <Table.Column flexGrow={0} width={80}>
          <Table.HeaderCell> </Table.HeaderCell>
          <DisableCell />
        </Table.Column>
        <Table.Column flexGrow={0} width={50}>
          <Table.HeaderCell> </Table.HeaderCell>
          <ActionCell />
        </Table.Column>
      </Table>
    </>
  );
};
